import { c as _c } from "react/compiler-runtime";
import type { GraphOperationAddNode, GraphOperationPasteNode, GraphOperationResult, VaultKind } from '@onefootprint/request-types/dashboard';
import { CaptureEscKeydown, Dialog, type OnEscKeydownHandler, Tabs } from '@onefootprint/ui';
import sortBy from 'lodash/sortBy';
import { parseAsStringEnum, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEdit from '../../hooks/use-edit';
import { AddNodeButton } from './components/add-node-button';
import { PasteNodeButton } from './components/paste-node-button';
import useAvailableTabs from './hooks/use-available-tabs';
import useGetNewNodeContext, { type NewNodeGroup, newNodeGroupValues } from './hooks/use-get-new-node-context';
type Operation = GraphOperationAddNode | GraphOperationPasteNode;
type AddNodeDialogProps = {
  open: boolean;
  onClose: (result?: GraphOperationResult) => void;
  operations: Operation[];
  subject: VaultKind;
  defaultTab?: NewNodeGroup;
};
const AddNodeDialog = t0 => {
  const $ = _c(19);
  const {
    open,
    onClose,
    operations,
    subject,
    defaultTab: t1
  } = t0;
  const defaultTab = t1 === undefined ? "recipe" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "addNode"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  let t3;
  if ($[1] !== defaultTab) {
    t3 = parseAsStringEnum(Object.values(newNodeGroupValues)).withOptions({
      history: "replace"
    }).withDefault(defaultTab);
    $[1] = defaultTab;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [activeTab, setActiveTab] = useQueryState("add_node_tab", t3);
  let t4;
  if ($[3] !== onClose || $[4] !== setActiveTab) {
    t4 = result => {
      setTimeout(() => setActiveTab(null), 200);
      onClose(result);
    };
    $[3] = onClose;
    $[4] = setActiveTab;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleClose = t4;
  let t5;
  if ($[6] !== t) {
    t5 = t(_temp);
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = activeTab || "data";
  let t7;
  if ($[8] !== handleClose || $[9] !== operations || $[10] !== setActiveTab || $[11] !== subject || $[12] !== t6) {
    t7 = <AddNodeDialogContent operations={operations} onClose={handleClose} subject={subject} activeTab={t6} setActiveTab={setActiveTab} />;
    $[8] = handleClose;
    $[9] = operations;
    $[10] = setActiveTab;
    $[11] = subject;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== handleClose || $[15] !== open || $[16] !== t5 || $[17] !== t7) {
    t8 = <Dialog open={open} onClose={handleClose} title={t5} isConfirmation={true} isCentered={false} size="large">{t7}</Dialog>;
    $[14] = handleClose;
    $[15] = open;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
type AddNodeDialogContentProps = {
  operations: Operation[];
  onClose: (result?: GraphOperationResult) => void;
  subject: VaultKind;
  activeTab: NewNodeGroup;
  setActiveTab: (tab: NewNodeGroup) => void;
  parentNodeId?: string;
  childNodeId?: string;
};
const AddNodeDialogContent = ({
  operations,
  onClose,
  subject,
  activeTab,
  setActiveTab
}: AddNodeDialogContentProps) => {
  const mutation = useEdit();
  const [processingOperation, setProcessingOperation] = useState<Operation | null>(null);
  const {
    getNodeContext
  } = useGetNewNodeContext();
  const pasteOperation = operations.find(op => op.kind === 'paste_node');
  const {
    availableTabs,
    currentActiveTab,
    groupedOperations
  } = useAvailableTabs({
    operations,
    activeTab
  });
  const attemptClose: OnEscKeydownHandler = {
    handler: () => {
      if (mutation.isPending) return;
      onClose();
    },
    deps: [mutation.isPending]
  };
  const handleAddNode = async (operation: Operation) => {
    setProcessingOperation(operation);
    try {
      const operationResult = await mutation.mutateAsync(operation);
      onClose(operationResult);
    } catch (error) {
      console.error('Error updating playbook session:', error);
    } finally {
      setProcessingOperation(null);
    }
  };
  return <>
      <CaptureEscKeydown id="add-node-drawer" onEscKeydown={attemptClose} />
      <div className="flex flex-col gap-6 pb-4">
        {pasteOperation && <PasteNodeButton operation={pasteOperation} onClose={onClose} subject={subject} />}
        {availableTabs.length > 0 && <div className="overflow-x-auto [&_button]:whitespace-nowrap md:overflow-visible md:[&_button]:whitespace-normal">
            <Tabs options={availableTabs} onChange={setActiveTab} defaultValue={currentActiveTab} />
          </div>}
        <div className="flex flex-col gap-3">
          {sortBy(groupedOperations[currentActiveTab] || [], op_0 => getNodeContext(op_0.nodeKind).priority).map(operation_0 => {
          const isProcessing = processingOperation === operation_0;
          const {
            title,
            description,
            icon,
            isBeta
          } = getNodeContext(operation_0.nodeKind);
          return <AddNodeButton description={description} disabled={mutation.isPending} icon={icon} isProcessing={isProcessing} key={operation_0.nodeKind} onClick={() => handleAddNode(operation_0)} title={title} isBeta={isBeta} />;
        })}
        </div>
      </div>
    </>;
};
export default AddNodeDialog;
function _temp($) {
  return $.title;
}